import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Clock, RefreshCw, Eye, ChevronRight, Loader2, LogOut, AlertTriangle, Zap, KeyRound, List } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getBuyerOrders, getBuyerQuotes, reorderApi, getQuickReorderData, quickReorderApi, buyerChangePassword } from '../../lib/api';
import { Toaster, toast } from 'sonner';

export default function BuyerDashboard() {
  const { buyerInfo, buyerLogout, cartCount, refreshCart, t, language } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickReorder, setQuickReorder] = useState({ categories: [], loading: true });
  const [reorderingCat, setReorderingCat] = useState(null);

  useEffect(() => {
    fetchData();
    fetchQuickReorder();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, quotesRes] = await Promise.all([
        getBuyerOrders(10),
        getBuyerQuotes(10),
      ]);
      setOrders(ordersRes.data);
      setQuotes(quotesRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuickReorder = async () => {
    try {
      const res = await getQuickReorderData();
      setQuickReorder({ categories: res.data.top_categories || [], loading: false });
    } catch (err) {
      setQuickReorder({ categories: [], loading: false });
    }
  };

  const handleQuickReorder = async (category) => {
    setReorderingCat(category);
    try {
      const res = await quickReorderApi({ category, mode: 'last_order' });
      const msg = res.data.skipped_items?.length
        ? `${t('buyerDash.cartUpdated')} ${res.data.items_added} ${t('common.items')} (${res.data.skipped_items.length} ${t('buyerDash.unavailable')})`
        : `${t('buyerDash.cartUpdated')} ${res.data.items_added} ${t('common.items')}`;
      toast.success(msg);
      refreshCart();
      navigate('/buyer/cart');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('buyerDash.failedReorder'));
    } finally {
      setReorderingCat(null);
    }
  };

  const handleReorder = async (orderId) => {
    try {
      const res = await reorderApi(orderId);
      toast.success(`${t('buyerDash.cartUpdated')} ${res.data.cart_count} ${t('common.items')}`);
      refreshCart();
      navigate('/buyer/cart');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('buyerDash.failedReorder'));
    }
  };

  const handleLogout = () => {
    buyerLogout();
    navigate('/buyer/login');
  };

  const statusColors = {
    pending_approval: 'bg-[#F59E0B]/20 text-[#F59E0B]',
    proforma_sent: 'bg-[#00D4FF]/20 text-[#00D4FF]',
    awaiting_wire: 'bg-purple-500/20 text-purple-400',
    paid: 'bg-[#10B981]/20 text-[#10B981]',
    processing: 'bg-blue-500/20 text-blue-400',
    shipped: 'bg-[#10B981]/20 text-[#10B981]',
  };

  if (buyerInfo?.role === 'buyer_suspended') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="buyer-suspended">
        <div className="card-dark p-12 text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-[#F59E0B] mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{t('buyerDash.accountSuspended')}</h2>
          <p className="text-white/60 mb-6">{t('buyerDash.accountSuspendedMsg')}</p>
          <button onClick={handleLogout} className="btn-secondary">{t('buyerDash.signOut')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="buyer-dashboard">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold" data-testid="buyer-company-name">
              {buyerInfo?.company_name || t('buyerDash.browseInventory')}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-white/60">{buyerInfo?.contact_name}</span>
              <span className="px-2 py-0.5 text-xs font-medium bg-[#10B981]/20 text-[#10B981]" data-testid="buyer-status-badge">
                {buyerInfo?.role === 'buyer_approved' ? t('buyerDash.approved') : t('buyerDash.pending')}
              </span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-white/60 hover:text-red-400 transition-colors self-start"
            data-testid="buyer-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {t('buyerDash.signOut')}
          </button>
        </div>

        {/* CTA Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <Link
            to="/products"
            className="card-dark p-6 flex items-center gap-4 hover:border-[#00D4FF]/30 transition-colors group"
            data-testid="browse-inventory-btn"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF]">
              <Package className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-semibold group-hover:text-[#00D4FF] transition-colors">{t('buyerDash.browseInventory')}</div>
              <div className="text-white/50 text-sm">{t('buyerDash.viewWholesalePricing')}</div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#00D4FF]" />
          </Link>

          <Link
            to="/buyer/cart"
            className="card-dark p-6 flex items-center gap-4 hover:border-[#00D4FF]/30 transition-colors group"
            data-testid="view-cart-btn"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF] relative">
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-[#00D4FF] text-black text-xs font-bold rounded-full">
                  {cartCount}
                </span>
              )}
            </div>
            <div className="flex-1">
              <div className="font-semibold group-hover:text-[#00D4FF] transition-colors">{t('buyerDash.viewCart')}</div>
              <div className="text-white/50 text-sm">{cartCount} {cartCount === 1 ? t('buyerDash.itemInCart') : t('buyerDash.itemsInCart')}</div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#00D4FF]" />
          </Link>

          <Link
            to="/buyer/saved-lists"
            className="card-dark p-6 flex items-center gap-4 hover:border-[#00D4FF]/30 transition-colors group"
            data-testid="saved-lists-btn"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#00D4FF]/10 text-[#00D4FF]">
              <List className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-semibold group-hover:text-[#00D4FF] transition-colors">{t('savedLists.title')}</div>
              <div className="text-white/50 text-sm">{language === 'es' ? 'Plantillas de pedido guardadas' : 'Saved order templates'}</div>
            </div>
            <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#00D4FF]" />
          </Link>
        </div>

        {/* Quick Reorder Widget */}
        <div className="mb-10" data-testid="quick-reorder-widget">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#00D4FF]" />
            {t('buyerDash.quickReorder')}
          </h2>
          {quickReorder.loading ? (
            <div className="card-dark p-6 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-[#00D4FF]" />
            </div>
          ) : quickReorder.categories.length === 0 ? (
            <div className="card-dark p-6 text-center" data-testid="quick-reorder-empty">
              <p className="text-white/50 mb-3">{t('buyerDash.noOrderHistory')}</p>
              <Link to="/products" className="text-[#00D4FF] hover:text-[#00A0FF] text-sm font-medium" data-testid="browse-inventory-link">
                {t('buyerDash.browseInventoryLink')} &rarr;
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {quickReorder.categories.map((cat) => (
                <div key={cat.category} className="card-dark p-4 flex flex-col justify-between" data-testid={`quick-reorder-${cat.category}`}>
                  <div>
                    <div className="font-semibold text-white mb-1">{cat.category}</div>
                    <div className="text-white/40 text-xs">
                      {cat.total_units} {t('common.units')} &middot; {new Date(cat.last_order_at).toLocaleDateString(language === 'es' ? 'es' : 'en')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleQuickReorder(cat.category)}
                    disabled={reorderingCat === cat.category}
                    className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-[#00D4FF]/10 text-[#00D4FF] hover:bg-[#00D4FF]/20 disabled:opacity-50 transition-colors"
                    data-testid={`quick-reorder-btn-${cat.category}`}
                  >
                    {reorderingCat === cat.category ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {t('buyerDash.reorder')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quote Requests */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4" data-testid="quote-requests-title">Quote Requests</h2>
          {loading ? (
            <div className="card-dark p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00D4FF]" />
            </div>
          ) : quotes.length === 0 ? (
            <div className="card-dark p-8 text-center" data-testid="no-quotes">
              <Clock className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/60">No quote requests yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {quotes.map((q) => (
                <div key={q.quote_id} className="card-dark p-4" data-testid={`quote-row-${q.quote_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="font-mono text-[#00D4FF] font-semibold">{q.quote_id}</div>
                      <div className="text-white/50 text-xs mt-0.5">
                        {new Date(q.created_at).toLocaleDateString(language === 'es' ? 'es' : 'en')} &middot; {q.item_count || q.items?.length || 0} {t('common.items')}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium ${
                        q.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                        q.status === 'under_review' ? 'bg-yellow-500/20 text-yellow-400' :
                        q.status === 'quote_sent' ? 'bg-purple-500/20 text-purple-400' :
                        q.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        q.status === 'converted_to_order' ? 'bg-cyan-500/20 text-cyan-400' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {q.status === 'new' ? 'New Request' :
                         q.status === 'under_review' ? 'Under Review' :
                         q.status === 'quote_sent' ? 'Quote Sent' :
                         q.status === 'approved' ? 'Approved' :
                         q.status === 'converted_to_order' ? 'Converted to Order' :
                         q.status}
                      </span>
                      <span className="font-mono font-bold text-[#00D4FF]">${(q.total_amount || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Orders (only real converted orders) */}
        {orders.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4" data-testid="recent-orders-title">Orders</h2>
            <div className="space-y-3">
              {orders.map((order) => (
                <div key={order.id} className="card-dark p-4" data-testid={`order-row-${order.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-mono text-[#00D4FF] font-semibold">{order.order_number}</div>
                        <div className="text-white/50 text-xs mt-0.5">
                          {new Date(order.created_at).toLocaleDateString(language === 'es' ? 'es' : 'en')} &middot; {order.items?.length || 0} {t('common.items')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium ${statusColors[order.status] || 'bg-white/10 text-white/60'}`}>
                        {t(`status.${order.status}`) || order.status}
                      </span>
                      <span className="font-mono font-bold text-[#00D4FF]">${order.total_amount?.toLocaleString()}</span>
                      <Link
                        to={`/buyer/orders/${order.id}`}
                        className="flex items-center gap-1 px-3 py-1 text-xs bg-white/5 hover:bg-[#00D4FF]/10 text-white/70 hover:text-[#00D4FF] transition-colors"
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="w-3 h-3" />
                        {t('common.view')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
        </div>
        )}

        {/* Saved Preferences */}
        {buyerInfo?.interested_categories?.length > 0 && (
          <div data-testid="saved-preferences">
            <h2 className="text-xl font-bold mb-4">{t('buyerDash.savedPreferences')}</h2>
            <div className="card-dark p-5">
              <div className="text-white/50 text-sm mb-2">{t('buyerDash.interestedCategories')}</div>
              <div className="flex flex-wrap gap-2">
                {buyerInfo.interested_categories.map((cat) => (
                  <span key={cat} className="px-3 py-1 text-sm bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20">
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Change Password */}
        <BuyerChangePasswordSection t={t} />
      </div>
    </div>
  );
}

const BuyerChangePasswordSection = ({ t }) => {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error(t('changePassword.mismatch'));
      return;
    }
    if (newPw.length < 8) {
      toast.error(t('changePassword.tooShort'));
      return;
    }
    setSaving(true);
    try {
      await buyerChangePassword({ current_password: currentPw, new_password: newPw });
      toast.success(t('changePassword.success'));
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="buyer-change-password">
      <h2 className="text-xl font-bold mb-4">{t('changePassword.title')}</h2>
      <div className="card-dark p-6">
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm text-white/60 mb-1">{t('changePassword.currentPassword')}</label>
            <input
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
              className="w-full px-4 py-2.5 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm"
              data-testid="buyer-current-password"
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">{t('changePassword.newPassword')}</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2.5 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm"
              data-testid="buyer-new-password"
            />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">{t('changePassword.confirmPassword')}</label>
            <input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2.5 bg-black border border-white/10 focus:border-[#00D4FF] text-white text-sm"
              data-testid="buyer-confirm-password"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#F59E0B] text-black font-semibold hover:bg-[#F59E0B]/90 transition-colors disabled:opacity-50"
            data-testid="buyer-change-password-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {saving ? t('changePassword.updating') : t('changePassword.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};
